import React from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';

export const SEND_PLATE_TO_ANALYSIS_BUTTON_TITLE = 'Send to analysis';

export interface SendPlateToAnalysisButtonProps {
  plateId: string;
  buttonStyle: ButtonStyle;
  buttonSize?: ButtonSize;
  className?: string;
}

function handleCreateAnalysis(plateId: string, params: CreateAnalysisParams): Promise<{analysisId: string}> {
  return AnalysisActions.createAnalysis(params).then(({analysisId}) =>
    AnalysisActions.addOrReplaceAndRunAnalysisStep(analysisId, {
      stepType: PipelineStepType.IMPORT_PLATES,
      stepVersion: 0,
      stepConfig: {scratchPlateIds: [plateId]},
    })
      .then((_) => {
        FileBrowserActions.openFile(analysisId);
        return {analysisId};
      })
      .catch((error) => {
        AnalysisActions.archiveAnalysis(
          analysisId,
          'Plate could not be added to the newly created analysis',
          null
        );
        throw error;
      })
  );
}

/** @package */
export function openSendPlateToAnalysisModal(plateId: string): void {
  import('analysis-tool/create-analysis-modal/CreateAnalysisModal').then(({CreateAnalysisModal}) =>
    legacyRunModal(CreateAnalysisModal, {
      title: 'Send to analysis',
      onCreateAnalysis: (params: CreateAnalysisParams) => handleCreateAnalysis(plateId, params),
      actionOrigin: ActionOrigin.INVENTORY_PLATE_PAGE_TOOLBAR,
    })
  );
}

export default function SendPlateToAnalysisButton(props: SendPlateToAnalysisButtonProps): JSX.Element {
  return (
    <Tooltip title={SEND_PLATE_TO_ANALYSIS_BUTTON_TITLE}>
      <Button
        aria-label={SEND_PLATE_TO_ANALYSIS_BUTTON_TITLE}
        buttonStyle={props.buttonStyle}
        className={props.className}
        icon={ModelTypeConstants.ANALYSIS}
        onClick={() => {
          openSendPlateToAnalysisModal(props.plateId);
        }}
        size={props.buttonSize ?? ButtonSize.SMALL}
        dataTestAttrs={{component: 'SendPlateToAnalysisButton', element: 'button'}}
      />
    </Tooltip>
  );
}
